import _ from 'lodash';
import * as esriLang from '@arcgis/core/core/lang.js';
import FieldNames from '../defaults/FieldNames.json';
import AuditLogService from '../services/AuditLogService';
import CancelService from '../services/CancelService';
import FeatureService from '../services/FeatureService';
import FundingService from '../services/FundingService';
import GeometryService from '../services/GeometryService';
import IDService from '../services/IDService';
import { formatDateForDisplay, getSaveableDate} from './DateHelper';
import { recalcProportionalFundingAcres, totalFundingAmount } from './FundingHelper';
import { getStatus, getAllMissingFieldsCount } from './FeatureHelper';
import type Graphic from '@arcgis/core/Graphic';

export async function processSubstitution({
    featureAttr,
    editableFeature,
    cancelReasonSelection,
    cancelSubReasonSelection,
    cancelHasSubstitute,
    cancelSubstituteSelection,
    cancelReasoning,
    userInfo,
    canceled,

    isNewSubstituteFeature = false,
}) {
    const { featureToReturn, cancelObject, substituteFeatureUpdate } = await createCancelFeature(
        featureAttr,
        editableFeature,
        cancelReasonSelection,
        cancelSubReasonSelection,
        cancelHasSubstitute,
        cancelSubstituteSelection,
        userInfo,
        canceled,
        isNewSubstituteFeature
    );

    if (canceled) {
        // Persist the IsCanceled/Status/CancelledDate changes on the
        // original feature. Use the merged attributes returned from
        // createCancelFeature (rather than the raw, unmerged featureAttr
        // param) so the cancel mixin actually gets saved.
        const mergedFeatureAttr = featureToReturn?.attributes ?? featureToReturn;
        await FeatureService.updateFeatureAttributes([mergedFeatureAttr]);
    }

    // Persist the cancel/substitution tracking entry (which carries the
    // HasSubstitute flag for the ORIGINAL feature) whenever the feature is
    // being canceled AND/OR a substitute was selected for it - a
    // substitution can happen without the original feature being fully
    // canceled, e.g. when only part of its funding moves to the substitute.
    if (canceled || cancelHasSubstitute) {
        const cancelCheckClone = _.cloneDeep(featureAttr[FieldNames.featureFields.isCanceled]);
        if (cancelCheckClone && cancelCheckClone === FieldNames.yesNo.yes) {
            // Update
            await CancelService.updateCanceledFeatureEntry(cancelObject);
        } else {
            // New Cancel
            await CancelService.addCanceledFeatureEntry(cancelObject);
        }
        await addCancelAuditLogEntry(
            canceled,
            cancelReasonSelection,
            cancelSubReasonSelection,
            cancelHasSubstitute,
            cancelSubstituteSelection,
            featureAttr,
            cancelReasoning,
            userInfo
        );
    }

     // parent features no longer have to be canceled if there is a substitution, only when ALL funding is moved to a substitute feature
    if (cancelHasSubstitute) {
        // substituteFeatureUpdate (built once in createCancelFeature) already
        // merges the IsSubstitute flag together with the origin's approval
        // fields copied over - a single updateFeatureAttributes call here
        // instead of one for the flag and a separate one for the approvals.
        await FeatureService.updateFeatureAttributes([substituteFeatureUpdate]);

        const subAuditLogMessage = ` Action is a substitute for ${
            featureAttr[FieldNames.featureFields.class]
        } ${featureAttr[FieldNames.featureFields.objectID]}. Substitute joinID: ${cancelSubstituteSelection[FieldNames.featureFields.joinID]}, joinFeature: ${cancelSubstituteSelection[FieldNames.featureFields.joinFeature]}.`;
        await AuditLogService.addAuditLogEntry(
            cancelSubstituteSelection,
            cancelSubstituteSelection[FieldNames.featureFields.joinID],
            cancelSubstituteSelection[FieldNames.featureFields.joinFeature],
            userInfo[FieldNames.userFields.nifcUsername],
            subAuditLogMessage
        );
    }

    return featureToReturn;
}


export async function processUncancel(featureAttr, editableFeature, cancelFeatureEntry, userInfo, isTreatment) {
    const FF = FieldNames.featureFields;
    const CF = FieldNames.cancelFields;

    const featureAttrWithoutCancelDate = {
        ...featureAttr,
        [FieldNames.featureFields.statusReason]:
            `Uncancelled on ${formatDateForDisplay(new Date())}`,
        [FF.cancelledDate]: null,
    };
    const missingFieldsCount = getAllMissingFieldsCount(
        featureAttrWithoutCancelDate,
        isTreatment,
        false,
        [
            FieldNames.featureFields.wbsID,
        ] //todo: this will need to be re-added at some point
    );
    const recalculatedStatus = getStatus(
        featureAttrWithoutCancelDate,
        featureAttrWithoutCancelDate[FF.hasActual],
        false,
        missingFieldsCount
    );

    const updatedFeatureAttr = {
        ...featureAttrWithoutCancelDate,
        [FF.isCanceled]: FieldNames.yesNo.no,
        [FF.status]: recalculatedStatus,
        [FF.modifiedBy]: userInfo[FieldNames.userFields.nifcUsername],
        [FF.lastModifiedDate]: getSaveableDate(new Date()),
    };
    await FeatureService.updateFeatureAttributes([updatedFeatureAttr]);

    // Remove the cancel reason, cancel sub-reason, and cancelled date from
    // the existing cancel record - rather than deleting the record outright,
    // clear out the fields that describe *why*/*when* it was canceled so it
    // no longer reflects an active cancellation.
    if (cancelFeatureEntry) {
        const cancelUpdateObj = {
            [FF.objectID]: cancelFeatureEntry[FF.objectID],
            [CF.cancelReason]: null,
            [CF.cancelSubReason]: null,
            [FF.cancelledDate]: null,
            [FF.modifiedBy]: userInfo[FieldNames.userFields.nifcUsername],
            [FF.lastModifiedDate]: getSaveableDate(new Date()),
        };
        await CancelService.updateCanceledFeatureEntry(cancelUpdateObj);
    }

    await AuditLogService.addAuditLogEntry(
        featureAttr,
        featureAttr[FF.joinID],
        featureAttr[FF.joinFeature],
        userInfo[FieldNames.userFields.nifcUsername],
        'Action was un-canceled.'
    );

    let featureToReturn = { ...updatedFeatureAttr };
    if (editableFeature?.attributes) {
        featureToReturn = esriLang.clone(editableFeature);
        featureToReturn.attributes = updatedFeatureAttr;
    }
    return featureToReturn;
}

export async function processSubstituteNewAction(
    editableFeature,
    selectedFundingIds,
    initFunding,
    updatedFunding,
    userInfo,
    isTreatment,
    nameSuffix = '_SUBSTITUTE',
    remainingFundingRows = null

) {
    // generate the new feature
    const newFeatureAttributes = await createNewSubstitutionFeature(
        editableFeature,
        nameSuffix,
        userInfo,
        isTreatment
    );

    const fundingFields = FieldNames.fundingFields;
    const updateFundingFields = [
        fundingFields.personnelCost,
        fundingFields.equipmentCost,
        fundingFields.materialAndSupplyCost,
        fundingFields.procurementCost,
        fundingFields.travelCost,
        fundingFields.grantsFixedCost,
        fundingFields.assetCost,
        fundingFields.contractualCost,
        fundingFields.otherCost,
    ];
    // Prefer the fully-computed remaining funding rows (costs subtracted and
    // Acres already recalculated proportionally by the SubstituteNewAction dialog,
    // matching the bulk-edit funding reallocation logic). Fall back to the legacy
    // subtraction-only calculation for backwards compatibility.
    const updatedOriginalFunding =
        remainingFundingRows ??
        updateOriginalFundingRows(initFunding, updatedFunding, updateFundingFields);
    // update the funding on the original feature to reflect the moved funding amounts
    await FundingService.updateFundingEntries(updatedOriginalFunding);

    const selectedIdSet = new Set((selectedFundingIds || []).map(Number));
    const selectedFundingRows = (updatedFunding || []).filter((row) =>
        selectedIdSet.has(Number(row[fundingFields.id] ?? row[fundingFields.objectID]))
    );

    if (selectedFundingRows.length > 0) {
        // add the new funding rows to the new feature
        await saveUpdatedFundingRows(
            selectedFundingRows,
            newFeatureAttributes[FieldNames.featureFields.joinID],
            newFeatureAttributes[FieldNames.featureFields.joinFeature],
            userInfo
        );
    }

    // if no funding is left on the original feature, cancel it and add a cancel reason
    const isPostFire = editableFeature?.attributes?.[FieldNames.featureFields.entityType] === 'PostFire';
    const shouldCancel =
        updatedOriginalFunding.reduce((acc, curr) => acc + totalFundingAmount(curr, isPostFire), 0) === 0;

    const featureToReturn = await processSubstitution({
        featureAttr: editableFeature.attributes,
        editableFeature,
        cancelReasonSelection: shouldCancel ? 'Substituted with New Action' : null,
        cancelSubReasonSelection: '',
        cancelHasSubstitute: true,
        cancelSubstituteSelection: newFeatureAttributes,
        cancelReasoning: `Action was substituted with a new action: ${newFeatureAttributes[FieldNames.featureFields.name]}.`,
        userInfo,
        canceled: shouldCancel,
        isNewSubstituteFeature: true,
    });

    // return both the (possibly canceled) original feature and the newly
    // created substitute feature, so callers can route to the new feature
    return { featureToReturn, newFeatureAttributes };
}

export async function processSubstituteExistingAction(
    editableFeature,
    selectedAction,
    selectedFundingIds,
    updatedFunding,
    userInfo,
    remainingFundingRows
) {
    const fundingFields = FieldNames.fundingFields;

    // Persist whatever the user left behind in the "Remaining Funding" grid
    // on the origin feature (costs already subtracted and Acres already
    // recalculated proportionally by the SubstituteExistingAction dialog,
    // matching the bulk-edit funding reallocation logic).
    await FundingService.updateFundingEntries(remainingFundingRows);

    const selectedIdSet = new Set((selectedFundingIds || []).map(Number));
    const selectedFundingRows = (updatedFunding || []).filter((row) =>
        selectedIdSet.has(Number(row[fundingFields.id] ?? row[fundingFields.objectID]))
    );

    if (selectedFundingRows.length > 0) {
        // Add the moved funding rows onto the EXISTING selected/target
        // action (rather than creating a brand-new feature, as is done for
        // Substitute with New Action). Since the target action may already
        // have its own funding lines, pull ALL of them first, combine with
        // the incoming (moved) rows, and recalculate the acreage
        // distribution across the FULL combined set - using the same
        // proportional-acreage logic used everywhere else in the app -
        // before saving, rather than only accounting for the newly moved
        // rows in isolation.
        await addFundingToExistingActionWithRecalculatedAcres(
            selectedFundingRows,
            selectedAction,
            userInfo
        );
    }

    // Note: approval fields (LocalApprovalDate, IsFunded, etc.) are copied
    // from the origin feature onto the existing target action as part of
    // the single substitute-feature update built in createCancelFeature/
    // processSubstitution below - no separate REST call is needed here.

    // if no funding is left on the original feature, cancel it and add a cancel reason
    const shouldCancel =
        (remainingFundingRows || []).reduce((acc, curr) => acc + totalFundingAmount(curr), 0) === 0;

    const featureToReturn = await processSubstitution({
        featureAttr: editableFeature.attributes,
        editableFeature,
        cancelReasonSelection: shouldCancel ? 'Substituted with Existing Action' : null,
        cancelSubReasonSelection: '',
        cancelHasSubstitute: true,
        cancelSubstituteSelection: selectedAction,
        cancelReasoning: `Action was substituted with an existing action: ${selectedAction[FieldNames.featureFields.name]}.`,
        userInfo,
        canceled: shouldCancel,
    });

    // return both the (possibly canceled) original feature and the existing
    // action that funding moved to, so callers can route to it
    return { featureToReturn, selectedAction };
}


export function buildSubstituteFeatureUpdate(
    originFeatureAttr,
    cancelSubstituteSelection,
    userInfo,
    isNewSubstituteFeature = false
) {
    const FF = FieldNames.featureFields;
    const approvalFields = [
        FF.isFunded,
        FF.fundedDate
    ];

    const substituteFeatureUpdate = {
        [FF.objectID]: cancelSubstituteSelection?.[FF.objectID],
        [FF.isSubstitute]: FieldNames.yesNo.yes,
        [FF.modifiedBy]: userInfo?.[FieldNames.userFields.nifcUsername],
        [FF.lastModifiedDate]: getSaveableDate(new Date()),
    };

    if (originFeatureAttr && !isNewSubstituteFeature) {
        for (const field of approvalFields) {
            substituteFeatureUpdate[field] = originFeatureAttr[field] ?? null;
        }
    }

    return substituteFeatureUpdate;
}

export function updateOriginalFundingRows(
    originalFundingRows: any,
    updatedFundingRows: any,
    fundingFields
) {
    const updatedFundingMap = new Map(
        (updatedFundingRows || []).map((row) => [
            row[fundingFields.id] ?? row[fundingFields.objectID],
            row,
        ])
    );
    // subtract updated funding row amounts from the original funding rows
    return (originalFundingRows || []).map((row) => {
        const rowId = row[fundingFields.id] ?? row[fundingFields.objectID];
        const updatedRow = updatedFundingMap.get(rowId);
        if (updatedRow) {
            // iterate over funding fields and get the difference between the original and updated values, defaulting to 0 if not present
            for (const field of fundingFields) {
                const originalValue = row[field] || 0;
                const updatedValue = updatedRow?.[field] || 0;
                row[field] = originalValue - updatedValue;
            }
        }
        return row;
    });
}

export function saveUpdatedFundingRows(selectedFundingRows, joinId, joinFeature, userInfo) {
    const fundingFields = FieldNames.fundingFields;
    const newFundingRows = selectedFundingRows.map((row) => {
        const {
            [fundingFields.objectID]: _objectId,
            [fundingFields.id]: _id,
            rowId,
            ...rest
        } = row;
        return {
            ...rest,
            [fundingFields.joinID]: joinId,
            [fundingFields.joinFeature]: joinFeature,
            [fundingFields.createdBy]: userInfo[FieldNames.userFields.nifcUsername],
            [fundingFields.createdDate]: getSaveableDate(new Date()),
            [fundingFields.modifiedBy]: userInfo[FieldNames.userFields.nifcUsername],
            [fundingFields.modifiedDate]: getSaveableDate(new Date()),
        };
    });
    return FundingService.addFundingEntries(newFundingRows);
}

/**
 * Move a set of funding rows onto an EXISTING target action/feature, taking
 * into account any funding lines the target already has. Rather than simply
 * adding the incoming rows (which would leave the target's Acres split
 * stale/incorrect once combined with its pre-existing funding), this:
 *   1. Fetches ALL of the target action's existing funding lines.
 *   2. Combines them with the incoming (moved) funding rows.
 *   3. Recalculates the acreage distribution across the FULL combined set,
 *      using the same proportional-acreage-distribution logic used
 *      everywhere else in the app (CalculateDefaultFundingAcres, via
 *      recalcProportionalFundingAcres).
 *   4. Saves the result - updating the target's existing funding rows with
 *      their newly recalculated Acres, and adding the incoming rows (with
 *      their own recalculated Acres) as new funding lines on the target.
 *
 * @param {Array<Record<string,any>>} selectedFundingRows - the funding rows moving onto the target
 * @param {Record<string,any>}        targetAction        - the existing feature/action receiving the funding
 * @param {Record<string,any>}        userInfo
 */
export async function addFundingToExistingActionWithRecalculatedAcres(
    selectedFundingRows,
    targetAction,
    userInfo
) {
    const fundingFields = FieldNames.fundingFields;
    const FF = FieldNames.featureFields;

    const targetJoinId = targetAction[FF.joinID];
    const targetJoinFeature = targetAction[FF.joinFeature];
    const isPostFire = targetAction?.[FF.entityType] === 'PostFire';
    const calculatedArea = targetAction?.[FF.calculatedArea] || 0;

    // 1. Get ALL existing funding lines already on the target action.
    const existingTargetFunding = (await FundingService.getFundingByJoinID(
        targetJoinId,
        targetJoinFeature
    )) || [];
    const existingRows = existingTargetFunding.map((row) => ({
        ...row,
        rowId: row[fundingFields.id] ?? row[fundingFields.objectID],
        __isNew: false,
    }));

    // 2. Add in the new (incoming/moved) funding, re-pointed at the target
    // action and stripped of the origin's identifiers.
    const newRows = selectedFundingRows.map((row) => {
        const {
            [fundingFields.objectID]: _objectId,
            [fundingFields.id]: _id,
            rowId,
            ...rest
        } = row;
        return {
            ...rest,
            [fundingFields.joinID]: targetJoinId,
            [fundingFields.joinFeature]: targetJoinFeature,
            [fundingFields.createdBy]: userInfo[FieldNames.userFields.nifcUsername],
            [fundingFields.createdDate]: getSaveableDate(new Date()),
            [fundingFields.modifiedBy]: userInfo[FieldNames.userFields.nifcUsername],
            [fundingFields.modifiedDate]: getSaveableDate(new Date()),
            __isNew: true,
        };
    });

    // 3. Recalculate the acreage distribution across the FULL combined set
    // of funding lines using the existing proportional-acreage methods.
    const combinedRows = existingRows.concat(newRows);
    const recalculatedRows = recalcProportionalFundingAcres(combinedRows, calculatedArea, isPostFire);

    const rowsToUpdate = recalculatedRows
        .filter((row) => !row.__isNew)
        .map(({ __isNew, rowId, ...rest }) => rest);
    const rowsToAdd = recalculatedRows
        .filter((row) => row.__isNew)
        .map(({ __isNew, rowId, ...rest }) => rest);

    // 4. Save - update the target's existing funding lines with their newly
    // recalculated Acres, and add the incoming rows as new funding lines.
    const savePromises = [];
    if (rowsToUpdate.length > 0) {
        savePromises.push(FundingService.updateFundingEntries(rowsToUpdate));
    }
    if (rowsToAdd.length > 0) {
        savePromises.push(FundingService.addFundingEntries(rowsToAdd));
    }
    await Promise.all(savePromises);
}

export async function createNewSubstitutionFeature(
    editableFeature: Graphic,
    nameSuffix: string,
    userInfo,
    isTreatment
) {
    const existingFeatureAttr = editableFeature?.attributes;
    if (!existingFeatureAttr) {
        return null;
    }
    const FF = FieldNames.featureFields;
    const geoType = existingFeatureAttr[FF.joinFeature];
    const geometry = editableFeature?.geometry;
    let newJoinId;
    if (!geometry) {
        return;
    }
    const geoAddResponse = await GeometryService.addGeometry(
        geometry,
        geoType,
        userInfo[FieldNames.userFields.nifcUsername]
    );
    const geometryAddResult = geoAddResponse?.addResults?.[0];
    if (!geometryAddResult?.success || geometryAddResult.objectId == null) {
        throw new Error('Failed to create substitute geometry');
    }
    newJoinId = await IDService.getGeometryID(geometryAddResult.objectId, geoType);
    if (newJoinId == null) {
        throw new Error('Failed to resolve substitute geometry ID');
    }

    // 2. Build the new (substitute) feature's attributes, copied "as-is"
    const newFeatureAttributes = _.cloneDeep(existingFeatureAttr);
    newFeatureAttributes[FF.joinID] = newJoinId;
    newFeatureAttributes[FF.joinFeature] = geoType;
    newFeatureAttributes[FF.name] = `${existingFeatureAttr[FF.name]}${nameSuffix}`;
    newFeatureAttributes[FF.isCanceled] = FieldNames.yesNo.no;
    newFeatureAttributes[FF.isSubstitute] = FieldNames.yesNo.yes;
    newFeatureAttributes[FF.cancelledDate] = null;
    newFeatureAttributes[FF.createdBy] = userInfo[FieldNames.userFields.nifcUsername];
    newFeatureAttributes[FF.createdOnDate] = getSaveableDate(new Date());
    newFeatureAttributes[FF.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
    newFeatureAttributes[FF.lastModifiedDate] = getSaveableDate(new Date());
    // clear approval fields on new feature and recalculate status based on the new funding and approval fields copied from the original feature
    const approvalFields = [
        FieldNames.featureFields.localApprovalDate,
        FieldNames.featureFields.regionalApprovalDate,
        FieldNames.featureFields.agencyApprovalDate,
        FieldNames.featureFields.departmentApprovalDate,
    ];
    for (const field of approvalFields) {
        newFeatureAttributes[field] = null;
    }
    const missingFieldsCount = getAllMissingFieldsCount(newFeatureAttributes, isTreatment, false);
    newFeatureAttributes[FieldNames.featureFields.status] = getStatus(
        newFeatureAttributes,
        newFeatureAttributes[FieldNames.featureFields.hasActual],
        false,
        missingFieldsCount
    );

    // 3. Create the new substitute feature
    const newFeatureResp = await FeatureService.copyFeatureAttributes(newFeatureAttributes);
    const addResult = newFeatureResp?.addResults?.[0];
    if (!addResult || !addResult.success) {
        throw new Error('Failed to create the substitute feature');
    }
    newFeatureAttributes[FieldNames.featureFields.objectID] = addResult.objectId;
    return newFeatureAttributes;
}

export async function createCancelFeature(
    featureAttr,
    editableFeature,
    cancelReasonSelection,
    cancelSubReasonSelection,
    cancelHasSubstitute,
    cancelSubstituteSelection,
    userInfo,
    canceled,
    isNewSubstituteFeature = false
) {
    let cancelObject = {
        JoinID: featureAttr[FieldNames.featureFields.joinID],
        JoinIDS: cancelSubstituteSelection?.[FieldNames.featureFields.joinID],
        JoinFeature: featureAttr[FieldNames.featureFields.joinFeature],
        JoinFeatureS: cancelSubstituteSelection?.[FieldNames.featureFields.joinFeature],
        EntityID: featureAttr[FieldNames.featureFields.entityID],
        EntityType: featureAttr[FieldNames.featureFields.entityType],
        CancelReason: cancelReasonSelection ? cancelReasonSelection : null,
        CancelSubReason: cancelSubReasonSelection ? cancelSubReasonSelection : null,
        HasSubstitute: cancelHasSubstitute ? FieldNames.yesNo.yes : FieldNames.yesNo.no,
        SubstituteEntityType: cancelHasSubstitute
            ? cancelSubstituteSelection[FieldNames.featureFields.entityType]
            : '',
        SubstituteEntityID: cancelHasSubstitute
            ? cancelSubstituteSelection[FieldNames.featureFields.entityID]
            : '',
        ModifiedBy: userInfo[FieldNames.userFields.nifcUsername],
        ModifiedDate: getSaveableDate(new Date()),
        IsArchived: FieldNames.yesNo.no,
    };

    if (featureAttr[FieldNames.featureFields.isCanceled] === FieldNames.yesNo.yes) {
        // Feature has already been canceled before
        const cancelObj = await CancelService.getFeatureCancelReason(
            featureAttr[FieldNames.featureFields.joinFeature],
            featureAttr[FieldNames.featureFields.joinID]
        );
        cancelObject[FieldNames.featureFields.objectID] =
            cancelObj[FieldNames.featureFields.objectID];
    } else {
        cancelObject[FieldNames.featureFields.createdBy] =
            userInfo[FieldNames.userFields.nifcUsername];
        cancelObject[FieldNames.featureFields.createdOnDate] = getSaveableDate(new Date());
    }

    const substituteFeatureUpdate = cancelHasSubstitute
        ? buildSubstituteFeatureUpdate(
              featureAttr,
              cancelSubstituteSelection,
              userInfo,
              isNewSubstituteFeature
          )
        : null;

    if (canceled) {
        featureAttr = {
            //mixin status reasons and mark as canceled
            ...featureAttr,
            [FieldNames.featureFields.isCanceled]: FieldNames.yesNo.yes,
            [FieldNames.featureFields.status]: FieldNames.statuses.cancelled,
            [FieldNames.featureFields.statusReason]: cancelHasSubstitute ? "Substitution Created" : null,
            [FieldNames.featureFields.cancelledDate]: getSaveableDate(new Date())
        };
    }
    let featureToReturn = { ...featureAttr };
    if (editableFeature?.attributes) {
        // Esri feature shape — preserve wrapper
        featureToReturn = esriLang.clone(editableFeature);
        featureToReturn.attributes = featureAttr;
    }
    return { featureToReturn, cancelObject, substituteFeatureUpdate };
}

export function addCancelAuditLogEntry(
    canceled,
    cancelReasonSelection,
    cancelSubReasonSelection,
    cancelHasSubstitute,
    cancelSubstituteSelection,
    featureAttr,
    cancelReasoning,
    userInfo
) {
    let baseMessage = canceled
        ? `Action was marked as canceled for the following reason: ${cancelReasonSelection}` +
          (cancelSubReasonSelection ? `: ${cancelSubReasonSelection}.` : `.`)
        : cancelHasSubstitute
          ? 'Partial Substitution of Action.'
          : 'Action was marked as canceled.';

    if (cancelHasSubstitute) {
        let featureClass = cancelSubstituteSelection[FieldNames.featureFields.class];
        baseMessage =
            baseMessage +
            ` Action is substituted by ${featureClass} ${
                cancelSubstituteSelection[FieldNames.featureFields.objectID]
            }.`;
    }
    let auditLogMessage = cancelReasoning ? baseMessage + ' ' + cancelReasoning : baseMessage;
    return AuditLogService.addAuditLogEntry(
        featureAttr,
        featureAttr[FieldNames.featureFields.joinID],
        featureAttr[FieldNames.featureFields.joinFeature],
        userInfo[FieldNames.userFields.nifcUsername],
        auditLogMessage
    );
}
